/**
 * Platform Admin API Client
 *
 * API client for Control Plane endpoints.
 * Uses platform auth tokens instead of main app tokens.
 */
import { usePlatformAuthStore } from '@/stores/platform-auth';

const API_BASE_URL = '/api/v1/control-plane';

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export class PlatformApiError extends Error {
  status: number;
  response?: ApiResponse<unknown>;

  constructor(message: string, status: number, response?: ApiResponse<unknown>) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
    this.response = response;
  }
}

/**
 * Refresh platform access token using httpOnly cookie
 */
async function refreshPlatformToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.data?.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * Get auth headers from platform auth store
 */
function getPlatformAuthHeaders(): Record<string, string> {
  const { accessToken } = usePlatformAuthStore.getState();
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  return headers;
}

/**
 * Execute request with automatic 401 handling and token refresh
 */
async function withPlatformTokenRefresh<T>(
  makeRequest: (headers: Record<string, string>) => Promise<Response>,
  processResponse: (response: Response) => Promise<T>,
): Promise<T> {
  const { accessToken, setAccessToken, clearAuth } = usePlatformAuthStore.getState();
  const headers = getPlatformAuthHeaders();

  let response = await makeRequest(headers);

  // Handle 401 by attempting to refresh the token
  if (response.status === 401 && accessToken) {
    const newToken = await refreshPlatformToken();

    if (newToken) {
      setAccessToken(newToken);
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await makeRequest(headers);
    } else {
      // Refresh failed - session expired
      clearAuth();
      window.location.href = '/platform/login';
      throw new PlatformApiError('Session expired. Please log in again.', 401);
    }
  }

  return processResponse(response);
}

/**
 * Process JSON API response
 */
async function processJsonResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers?.get('content-type');

  let data: ApiResponse<T>;
  if (!contentType || contentType.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      throw new PlatformApiError(
        response.status === 401 ? 'Authentication failed' : 'Request failed',
        response.status,
      );
    }
  } else {
    throw new PlatformApiError(
      response.status === 401
        ? 'Authentication failed'
        : `Request failed with status ${response.status}`,
      response.status,
    );
  }

  if (!response.ok) {
    let errorMessage = 'An error occurred';
    if (typeof data.error === 'string') {
      errorMessage = data.error;
    } else if (data.error && typeof data.error === 'object') {
      const errorObj = data.error as { message?: string };
      errorMessage = errorObj.message ?? errorMessage;
    }
    throw new PlatformApiError(errorMessage, response.status, data);
  }

  return data;
}

/**
 * Fetch with platform auth
 */
async function fetchWithPlatformAuth<T>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<ApiResponse<T>> {
  const { body: optionsBody, ...restOptions } = options;

  return withPlatformTokenRefresh(
    (headers) => {
      const fetchHeaders = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
        ...headers,
      };

      const fetchOptions: RequestInit = {
        ...restOptions,
        headers: fetchHeaders,
        credentials: 'include',
      };

      if (optionsBody) {
        fetchOptions.body = JSON.stringify(optionsBody);
      }

      return fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    },
    processJsonResponse<T>,
  );
}

/**
 * Platform API client
 */
export const platformApi = {
  get: <T>(endpoint: string, options?: FetchOptions) =>
    fetchWithPlatformAuth<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithPlatformAuth<T>(endpoint, { ...options, method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithPlatformAuth<T>(endpoint, { ...options, method: 'PATCH', body }),

  put: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    fetchWithPlatformAuth<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: FetchOptions) =>
    fetchWithPlatformAuth<T>(endpoint, { ...options, method: 'DELETE' }),
};

// =============================================================================
// Auth-specific API calls (don't require token for login)
// =============================================================================

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  admin: {
    id: string;
    email: string;
    name: string;
    organizationId: string | null;
    isPrimary: boolean;
    isSuperAdmin: boolean;
  };
}

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  organizationId: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    status: string;
  } | null;
  isPrimary: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
  activeSessions: number;
}

/**
 * Login to platform (no auth required)
 */
export async function platformLogin(
  email: string,
  password: string,
): Promise<ApiResponse<LoginResponse>> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });

  return processJsonResponse<LoginResponse>(response);
}

/**
 * Logout from platform
 */
export async function platformLogout(): Promise<void> {
  const headers = getPlatformAuthHeaders();
  await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    headers,
    credentials: 'include',
  });
}

/**
 * Get current platform admin info
 */
export async function platformGetMe(): Promise<ApiResponse<MeResponse>> {
  return platformApi.get<MeResponse>('/auth/me');
}

/**
 * Refresh platform token
 */
export async function platformRefresh(): Promise<ApiResponse<LoginResponse>> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  });

  return processJsonResponse<LoginResponse>(response);
}
